import { FC, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store";
import useLoanManagement from "../../../../hooks/useLoanManagement";
import { LoanManagement } from "../../../../services/loanManagement";
import { LoanStatus } from "../../../../constant/enum";
import { toast } from "react-toastify";
import {
  MRT_ColumnDef,
  MRT_PaginationState,
  MRT_SortingState,
  MantineReactTable,
  useMantineReactTable,
} from "mantine-react-table";
import { formatNumbersWithThousandSeperators } from "@funded-here-interface/common/src/Utils/formatter";

const Application: FC = () => {
  const token = useSelector((state: RootState) => state.auth.token);
  const [data, setData] = useState<LoanManagement[]>([]);

  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [totalRowCount, setTotalRowCount] = useState(0);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const {
    data: loanData,
    isLoading,
    error,
  } = useLoanManagement(token, LoanStatus.APPLICATION, pagination, [], sorting);
  useEffect(() => {
    if (loanData) {
      setData(loanData.data);
      setTotalRowCount(loanData.meta.totalRowCount);
    }
    if (error) {
      toast.error((error as Error).message);
    }
  }, [loanData, isLoading, error]);

  const columns = useMemo<MRT_ColumnDef<LoanManagement>[]>(
    () => [
      {
        accessorKey: "skuId",
        header: "SKU ID",
      },
      {
        accessorKey: "upc",
        header: "UPC",
      },
      {
        accessorKey: "orgName",
        header: "Org Name",
      },
      {
        accessorKey: "distributorOrgName",
        header: "Supplier Name", // Distributor / Supplier
      },
      {
        accessorKey: "status",
        header: "Status",
      },
      {
        accessorKey: "availableCreditLine",
        header: "Available Credit Line",
        Cell: ({ row }) => {
          const currency = row?.original?.loanApplicationCurrency || "";
          const creditLine = formatNumbersWithThousandSeperators(
            parseFloat(row?.original?.availableCreditLine || "0")
          );
          return <p>{`${currency} ${creditLine}`}</p>;
        },
      },
      {
        accessorKey: "invoiceValue",
        header: "Invoice Value",
        Cell: ({ cell }) => <p>{cell.getValue<string>() || "-"}</p>,
      },
      {
        accessorKey: "doi",
        header: "DOI",
        Cell: ({ cell }) => <p>{cell.getValue<string>() || "-"}</p>,
      },
      {
        accessorKey: "grossMargin",
        header: "Gross Margin",
        Cell: ({ cell }) => <p>{cell.getValue<string>() || "-"}</p>,
      },
      {
        accessorKey: "skuReturn",
        header: "SKU Return",
        Cell: ({ cell }) => <p>{cell.getValue<string>() || "-"}</p>,
      },
      {
        accessorKey: "skuSalesVolume",
        header: "SKU Sales Volume",
        Cell: ({ cell }) => (
          <p>
            {cell.getValue<number>()
              ? formatNumbersWithThousandSeperators(cell.getValue<number>())
              : "-"}
          </p>
        ),
      },
      {
        accessorKey: "productSalesVolume",
        header: "Product Sales Volume",
        Cell: ({ cell }) => (
          <p>
            {cell.getValue<number>()
              ? formatNumbersWithThousandSeperators(cell.getValue<number>())
              : "-"}
          </p>
        ),
      },
      {
        accessorKey: "highestOrderedQuantityFromPastRecords",
        header: "Highest Ordered Quantity",
        Cell: ({ cell }) => (
          <p>
            {cell.getValue<number>()
              ? formatNumbersWithThousandSeperators(cell.getValue<number>())
              : "-"}
          </p>
        ),
      },
      {
        accessorKey: "paymentTerm",
        header: "Payment Terms",
        Cell: ({ cell }) => <p>{cell.getValue<string>() || "-"}</p>,
      },
    ],
    []
  );

  const table = useMantineReactTable({
    enableColumnActions: false,
    enableColumnFilters: false,
    manualPagination: true,
    manualSorting: true,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    paginationDisplayMode: "pages",
    state: { pagination, isLoading, sorting },
    rowCount: totalRowCount,
    columns,
    data: data,
    initialState: { density: "xs" },
    mantineTableProps: {
      withBorder: true,
    },
    mantineTableHeadCellProps: {
      style: {
        userSelect: "text",
      },
    },
  });

  return <MantineReactTable table={table} />;
};

export default Application;
